import cn from 'classnames'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { MANAGER_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/managerCockpitTourTargets'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { setCockpitRtm } from '../../model'
import { selectCockpitRtm, selectCockpitRtmCounts } from '../../model/selectors'
import { TCockpitRtm } from '../../model/types'
import { CockpitSegmentedControlAccent } from '../components/cockpitSegmentedControlAccent'

import styles from './CockpitRtm.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.rtm

export const CockpitRtm = ({
  embedded = false,
  compact = false,
}: {
  embedded?: boolean
  compact?: boolean
}) => {
  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const selectedRtm = useSelector(selectCockpitRtm)
  const { resellersCount, distributorsCount } = useSelector(selectCockpitRtmCounts)

  const onlyAvailableRtm: TCockpitRtm | null =
    resellersCount > 0 && distributorsCount === 0
      ? 'resellers'
      : distributorsCount > 0 && resellersCount === 0
        ? 'distributors'
        : null

  const handleSelect = useCallback(
    (rtm: TCockpitRtm) => {
      dispatch(setCockpitRtm(rtm))
    },
    [dispatch]
  )

  useEffect(() => {
    if (onlyAvailableRtm && selectedRtm !== onlyAvailableRtm) {
      dispatch(setCockpitRtm(onlyAvailableRtm))
    }
  }, [dispatch, onlyAvailableRtm, selectedRtm])

  if (onlyAvailableRtm) {
    return null
  }

  if (compact) {
    return (
      <div
        className={styles.compact}
        role="group"
        aria-label="RTM"
        data-tour-id={TOUR_TARGETS.filterRtm}
      >
        {(
          [
            { value: 'resellers', count: resellersCount, label: KEYS.resellers },
            { value: 'distributors', count: distributorsCount, label: KEYS.distributors },
          ] as const
        ).map(({ value, count, label }) => (
          <Tooltip
            key={value}
            tipContent={() => (
              <Translation
                span
                text={label}
                replaceParams={[{ key: '{count}', value: String(count) }]}
              />
            )}
          >
            <button
              type="button"
              className={cn(styles.badge, { [styles.active]: selectedRtm === value })}
              aria-label={t(label).replaceAll('{count}', String(count))}
              aria-pressed={selectedRtm === value}
              onClick={() => handleSelect(value)}
            >
              {count}
            </button>
          </Tooltip>
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn(styles.root, { [styles.embedded]: embedded })}
      data-tour-id={TOUR_TARGETS.filterRtm}
    >
      <CockpitSegmentedControlAccent stretched={embedded}>
        <SegmentedControl
          useItemKeys
          selectedItem={selectedRtm}
          onItemSelect={handleSelect}
          size="small"
          variant="veneer"
          isStreatched={embedded}
        >
          <SegmentedControl.Item itemKey="resellers">
            <Translation
              span
              text={KEYS.resellers}
              replaceParams={[{ key: '{count}', value: String(resellersCount) }]}
            />
          </SegmentedControl.Item>
          <SegmentedControl.Item itemKey="distributors">
            <Translation
              span
              text={KEYS.distributors}
              replaceParams={[{ key: '{count}', value: String(distributorsCount) }]}
            />
          </SegmentedControl.Item>
        </SegmentedControl>
      </CockpitSegmentedControlAccent>
    </div>
  )
}
